import is from '@sindresorhus/is';
import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';

import type { PaginatedSkillCatalog, SkillSyncStatus } from '~/shared/data-contract/skill';

import { jobArchitectureKeys } from '../../../job-architecture-v2/api/query-keys';

const skillCatalogSearchKey = [...jobArchitectureKeys.skillCatalog(), 'search'];

const findSyncStatus = (queryClient: QueryClient, skillId: string): SkillSyncStatus | undefined => {
    const caches = queryClient.getQueriesData<InfiniteData<PaginatedSkillCatalog>>({
        queryKey: skillCatalogSearchKey,
    });

    for (const [, data] of caches) {
        for (const page of data?.pages ?? []) {
            const entry = page.data.find((skill) => skill.id === skillId);

            if (!is.undefined(entry)) {
                return entry.syncStatus;
            }
        }
    }

    return undefined;
};

/**
 * The skill details endpoint does not carry the catalog sync status, so it is read back from the
 * skill catalog list the sidepanel was opened from. Undefined means the skill is not in any loaded
 * catalog page, in which case there is no status to show or change.
 */
export const useCachedSkillSyncStatus = (skillId: string): SkillSyncStatus | undefined => {
    const queryClient = useQueryClient();

    const subscribe = useCallback(
        (onStoreChange: () => void) =>
            queryClient.getQueryCache().subscribe((event) => {
                if (skillCatalogSearchKey.every((segment, index) => event.query.queryKey[index] === segment)) {
                    onStoreChange();
                }
            }),
        [queryClient],
    );

    const getSnapshot = useCallback(() => findSyncStatus(queryClient, skillId), [queryClient, skillId]);

    return useSyncExternalStore(subscribe, getSnapshot);
};
